/**
 * MANAGEMENT (15) — analyzer-fleet wire shapes. The EXACT client view of the CROSS-LAB, READ-ONLY analyzer
 * monitoring endpoints the super-admin fleet console consumes, verified against
 * server/src/modules/management/analyzers.service.js + analyzers.validators.js:
 *   GET /management/analyzers                       → {@link AnalyzerFleetPage}   (paginated fleet list)
 *   GET /management/analyzers/:automateId/health    → {@link AnalyzerHealth}      (health/usage drill-down)
 *
 * MONITORING-ONLY / L-4. This plane surfaces + monitors the fleet; it NEVER mutates a lab's LIS config (the
 * per-lab `lis` module owns Automate / AutomateConfig / LisAgent). No PHI rides either seam — the reads are
 * pure operational telemetry (result/error counts, TAT, uptime, heartbeat). Every id/count arrives as a plain
 * Number/String (the server closes the BigInt-serialization trap before res.json). These types are additive
 * to ../types.ts (they do NOT re-declare the shared {@link Page} envelope — the fleet list carries its own
 * `generatedAt` alongside the same page fields).
 */

/** The analyzer lifecycle statut — mirrors the server `ANALYZER_STATUTS` / `StatutAutomate` enum. */
export type AnalyzerStatut = "actif" | "maintenance" | "hors_service";

/** The list of statuts, in display order — the single source the filter <Select> + the i18n labels read. */
export const ANALYZER_STATUTS: ReadonlyArray<AnalyzerStatut> = ["actif", "maintenance", "hors_service"];

/** The operator-facing lab identity block (subset of Laboratoire) attached to every analyzer. */
export interface AnalyzerLab {
  id: string;
  reference: string | null;
  raisonSociale: string | null;
  nomCommercial: string | null;
  deployClass: string | null;
  wilaya: string | null;
}

/**
 * The protocol/transport pinning block (from AutomateConfig, null-safe). `configured:false` when the analyzer
 * has no config row yet. `fiabilite` is the per-analyzer reliability blob (JSON) surfaced verbatim.
 */
export interface AnalyzerTransport {
  configured: boolean;
  protocole: string | null;
  transport: string | null;
  transportMode: string | null;
  sens: string | null;
  modeInteraction: string | null;
  actif: boolean | null;
  fiabilite: Record<string, unknown>;
}

/**
 * The paired-agent heartbeat block (derived per-lab from the freshest LisAgent). `online` is the server's
 * freshness verdict against its reference clock; `lastHeartbeatAt` is null when no agent has ever checked in.
 */
export interface AnalyzerHeartbeat {
  hasAgent: boolean;
  lastHeartbeatAt: string | null;
  online: boolean;
  versionAgent: string | null;
}

/** One row in the cross-lab fleet list. */
export interface AnalyzerFleetRow {
  id: string;
  code: string;
  libelle: string;
  fabricant: string | null;
  modele: string | null;
  disciplineCode: string | null;
  profilKey: string | null;
  statut: AnalyzerStatut;
  version: number;
  createdAt: string;
  updatedAt: string;
  laboratoire: AnalyzerLab | null;
  transport: AnalyzerTransport;
  heartbeat: AnalyzerHeartbeat;
}

/** The paginated fleet-list payload (the shared page fields + a server-clock stamp). */
export interface AnalyzerFleetPage {
  items: AnalyzerFleetRow[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
  generatedAt: string;
}

/** The filters the fleet list accepts (snake_case is applied at the api-client seam). */
export interface AnalyzerFleetFilters {
  q?: string;
  laboratoireId?: string;
  disciplineCode?: string;
  statut?: AnalyzerStatut | "";
  /** Heartbeat online/offline narrowing — applied server-side post-enrichment. */
  online?: boolean;
  page?: number;
  pageSize?: number;
}

// ── /management/analyzers/:automateId/health ─────────────────────────────────────────

/** One day's health snapshot on the drill-down series (gap-tolerant — absent days simply are not present). */
export interface AnalyzerHealthPoint {
  /** ISO date (YYYY-MM-DD) — the day bucket. */
  jour: string;
  resultCount: number;
  errorCount: number;
  /** Avg turnaround (ms) for the day; null when no results carried a TAT. */
  avgTatMs: number | null;
  /** Uptime percentage (0..100) for the day; null when unmeasured. */
  uptimePct: number | null;
}

/** The rolled-up usage block over the trailing window + its gap-tolerant daily series. */
export interface AnalyzerHealthUsage {
  days: number;
  resultTotal: number;
  errorTotal: number;
  /** Error RATE over everything processed (0..100); null when nothing was processed. */
  errorRatePct: number | null;
  /** Result-weighted avg TAT (ms) over the window; null when unmeasured. */
  avgTatMs: number | null;
  /** Mean daily uptime (0..100) over the window; null when unmeasured. */
  avgUptimePct: number | null;
  lastMetricHeartbeatAt: string | null;
  /** Count of days that HAVE a snapshot (the series length). */
  snapshotDays: number;
  series: AnalyzerHealthPoint[];
}

/** The analyzer identity block on the drill-down (subset — no version/timestamps). */
export interface AnalyzerHealthIdentity {
  id: string;
  code: string;
  libelle: string;
  fabricant: string | null;
  modele: string | null;
  disciplineCode: string | null;
  profilKey: string | null;
  statut: AnalyzerStatut;
}

/** The full health/usage drill-down payload (self-contained: identity + lab + transport + heartbeat + usage). */
export interface AnalyzerHealth {
  found: true;
  analyzer: AnalyzerHealthIdentity;
  laboratoire: AnalyzerLab | null;
  transport: AnalyzerTransport;
  heartbeat: AnalyzerHeartbeat;
  usage: AnalyzerHealthUsage;
  generatedAt: string;
  windowStart: string;
}
